import { Link } from "react-router-dom";
import { Copy, Check, Bookmark, BookmarkCheck, Share2, ArrowRight } from "lucide-react";
import { useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { SubjectIcon, subjectGradient } from "@/components/SubjectIcon";
import { TIDS } from "@/constants/testIds";
import { motion } from "framer-motion";

export default function LOCard({ entry, index = 0 }) {
    const { t, subjectLabel, bookmarks, toggleBookmark } = useApp();
    const [copied, setCopied] = useState(false);
    const isBookmarked = bookmarks.includes(entry.code);
    const isOdia = /[\u0B00-\u0B7F]/.test(entry.description || "");

    const onCopy = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await navigator.clipboard.writeText(`${entry.code}\n\n${entry.description}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const onShare = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const text = `${entry.code} (${t.classN(entry.class)} • ${subjectLabel(entry.subject)})\n${entry.description}`;
        if (navigator.share) {
            try { await navigator.share({ title: entry.code, text }); } catch {}
        } else {
            await navigator.clipboard.writeText(text);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.3) }}
        >
            <Link
                to={`/lo/${encodeURIComponent(entry.code)}`}
                data-testid={TIDS.loCard(entry.code)}
                className="group relative block rounded-3xl bg-card border border-border/70 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5">
                        <div className={`h-9 w-9 rounded-2xl bg-gradient-to-br ${subjectGradient(entry.subject)} grid place-items-center text-white shadow-sm`}>
                            <SubjectIcon subject={entry.subject} className="h-4 w-4" />
                        </div>
                        <div>
                            <div className="font-display font-bold text-lg leading-tight tracking-tight">{entry.code}</div>
                            <div className="text-[11px] text-muted-foreground font-medium">
                                {t.classN(entry.class)} • {subjectLabel(entry.subject)}
                            </div>
                        </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                <p className={`text-sm text-foreground/85 leading-relaxed line-clamp-3 ${isOdia ? "font-odia" : ""}`}>
                    {entry.description}
                </p>

                <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-border/60">
                    <button
                        onClick={onCopy}
                        data-testid={TIDS.loCopyBtn(entry.code)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 active:scale-95 text-xs font-medium transition-all"
                    >
                        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        {copied ? t.copied : t.copy}
                    </button>
                    <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleBookmark(entry.code); }}
                        data-testid={TIDS.loBookmarkBtn(entry.code)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full active:scale-95 text-xs font-medium transition-all ${
                            isBookmarked ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" : "bg-muted hover:bg-muted/70"
                        }`}
                    >
                        {isBookmarked ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
                    </button>
                    <button
                        onClick={onShare}
                        data-testid={TIDS.loShareBtn(entry.code)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 active:scale-95 text-xs font-medium transition-all"
                    >
                        <Share2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </Link>
        </motion.div>
    );
}
